import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import LockIcon from "@mui/icons-material/Lock";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import styled from "styled-components";
import { Black, DarkGrey, Grey, OffWhite, White05 } from "./colors";
import { P3 } from "./text/Headings";

import { auth, db } from "../../services/firebase.config";
import { calculateDerivedFields } from "../../utils/derivedFields";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";

// Styled components
const XAxisContainer = styled.div`
  width: 100%;
  margin: 15px auto;
  margin-bottom: 30px;
  font-family: Arial, sans-serif;
`;

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px 0 0;
`;

const Label = styled.span`
  font-weight: 900;
  font-size: 16px;
  width: 45%;
  text-align: ${(props) => props.textAlign};
`;

const XAxisLine = styled.div`
  position: relative;
  height: 20px;
  background-color: ${Black};
  border-radius: 10px;
  margin: 10px 0 0;
`;

const Indicator = styled.div`
  position: absolute;
  top: -5px;
  transform: translateX(-50%);
  left: ${(props) => props.position};
`;

const ProfilePicture = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.$borderColor || Grey};
  object-fit: cover;
`;

const SecPic = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.$borderColor || Grey};
  background-color: ${DarkGrey};
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  color: ${OffWhite};
`;

const GradientCircle = styled.div`
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${DarkGrey};
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  color: ${OffWhite};
  z-index: 1;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 2px;
    border-radius: 50%;
    background: ${(props) => {
      if (props.gradientColors) {
        const { color1, color2 } = props.gradientColors;
        return `conic-gradient(from 0deg, ${color2} 0% 50%, ${color1} 50% 100%)`;
      }
      return DarkGrey;
    }};
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: -1;
  }
`;

const LockedCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  background: ${DarkGrey};
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 50%;
    background: ${(props) =>
      `linear-gradient(120deg, ${props.$background}, ${props.$transitionBackground})`};
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    border-radius: 50%;
    background: ${DarkGrey};
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const DescriptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 0 15px;
  font-size: 13px;
`;

const Description = styled.span`
  width: 45%;
  text-align: ${(props) => props.textAlign};
  color: ${Grey};
`;

const Axis = ({
  quizName,
  quizId,
  senderQuizId,
  leftLabel,
  rightLabel,
  leftDescription,
  rightDescription,
  displayName,
  profilePicture,
  profilePicture2,
  displayName2,
  field,
  onPercentageUpdate,
  colorOne,
  colorTwo,
}) => {
  const renderLabel = (label) => {
    if (typeof label === "string") {
      const parts = label.split(/<br\s*\/?>(?![^<]*>)/i);
      if (parts.length > 1) {
        return parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <br />}
            {part}
          </React.Fragment>
        ));
      }
    }
    return label;
  };

  const currentUser = auth.currentUser;

  const [position, setPosition] = useState("50%");
  const [position2, setPosition2] = useState("");
  const [loading, setLoading] = useState(true); // New loading state

  // Check if positions overlap (within 2% tolerance)
  const positionsOverlap = () => {
    if (!position2) return false;
    const pos1 = parseFloat(position);
    const pos2 = parseFloat(position2);
    return Math.abs(pos1 - pos2) <= 2; // 2% tolerance for overlap
  };

  // Get base fields for each quiz type
  const getBaseFields = (quizType) => {
    switch (quizType) {
      case "personality-check":
        return ["feeling", "introversion", "perception", "intuition"];
      case "best-date":
        return [
          "night",
          "social",
          "planner",
          "open",
          "direct",
          "effort",
          "boujee",
        ];
      case "political-alignment":
        return [
          "socialLib",
          "econLib",
          "globalism",
          "conservation",
          "public",
          "socialSupport",
        ];
      case "sex-styles":
        return ["dom", "bdsm", "voyeur", "exhibition", "casual", "rough"];
      case "am-i-gay":
        return [
          "same-sex",
          "same-gender",
          "queer-leaning",
          "at-ease",
          "queer-inclusive",
          "explorative",
        ];
      default:
        return [];
    }
  };

  // Helper function to calculate derived field values
  const tryCalculateDerivedField = async (quizData, targetField, quizType) => {
    // Get all base scores from the quiz data
    const baseScores = {};
    const baseFields = getBaseFields(quizType);

    baseFields.forEach((baseField) => {
      const matchingAnswers = Object.values(quizData.answers).filter(
        (item) => item.field === baseField
      );

      if (matchingAnswers.length > 0) {
        const totalPercentage = matchingAnswers.reduce(
          (acc, answer) => acc + answer.percentage,
          0
        );
        baseScores[baseField] = totalPercentage / matchingAnswers.length;
      }
    });

    // Calculate derived fields
    const derivedFields = calculateDerivedFields(quizType, baseScores);

    if (derivedFields[targetField] !== undefined) {
      const calculatedPercentage = derivedFields[targetField];
      setPosition(`${calculatedPercentage}%`);

      if (onPercentageUpdate) {
        onPercentageUpdate(
          targetField,
          calculatedPercentage,
          parseFloat(position2)
        );
      }

      return true;
    }

    return false;
  };

  // Helper function to calculate derived field values for second user (in shared quizzes)
  const tryCalculateDerivedFieldForSecondUser = async (
    quizData,
    targetField,
    quizType
  ) => {
    // Get all base scores from the quiz data
    const baseScores = {};
    const baseFields = getBaseFields(quizType);

    baseFields.forEach((baseField) => {
      const matchingAnswers = Object.values(quizData.answers).filter(
        (item) => item.field === baseField
      );

      if (matchingAnswers.length > 0) {
        const totalPercentage = matchingAnswers.reduce(
          (acc, answer) => acc + answer.percentage,
          0
        );
        baseScores[baseField] = totalPercentage / matchingAnswers.length;
      }
    });

    // Calculate derived fields
    const derivedFields = calculateDerivedFields(quizType, baseScores);

    if (derivedFields[targetField] !== undefined) {
      return derivedFields[targetField];
    }

    return null;
  };

  useEffect(() => {
    const fetchQuizResults = async () => {
      if (!currentUser || !quizId || !quizName) return;

      const docRef = doc(db, "quiz-answers", quizId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const matchingAnswers = Object.values(data.answers).filter(
          (item) => item.field === field
        );

        if (matchingAnswers.length > 0) {
          const totalPercentage = matchingAnswers.reduce(
            (acc, answer) => acc + answer.percentage,
            0
          );

          const averagePercentage = totalPercentage / matchingAnswers.length;
          setPosition(`${averagePercentage}%`);

          if (onPercentageUpdate) {
            onPercentageUpdate(field, averagePercentage, parseFloat(position2));
          }
        } else {
          // Try to calculate derived field if this is a locked field
          await tryCalculateDerivedField(data, field, quizName);
        }
      } else {
      }
    };

    fetchQuizResults();
    // eslint-disable-next-line
  }, [currentUser, field, quizId, quizName, onPercentageUpdate]);

  useEffect(() => {
    const fetchSecondQuizResults = async () => {
      if (!senderQuizId || !quizId || !quizName) return;

      const docRef = doc(db, "quiz-answers", senderQuizId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const matchingAnswers = Object.values(data.answers).filter(
          (item) => item.field === field
        );

        if (matchingAnswers.length > 0) {
          const totalPercentage = matchingAnswers.reduce(
            (acc, answer) => acc + answer.percentage,
            0
          );
          const averagePercentage = totalPercentage / matchingAnswers.length;
          setPosition2(`${averagePercentage}%`);

          // Update the parent
          if (onPercentageUpdate) {
            onPercentageUpdate(field, parseFloat(position), averagePercentage);
          }
        } else {
          // Try to calculate derived field for second user
          const calculatedPosition2 =
            await tryCalculateDerivedFieldForSecondUser(data, field, quizName);
          if (calculatedPosition2 !== null) {
            setPosition2(`${calculatedPosition2}%`);
            if (onPercentageUpdate) {
              onPercentageUpdate(
                field,
                parseFloat(position),
                calculatedPosition2
              );
            }
          }
        }
      } else {
      }
    };

    fetchSecondQuizResults();
    setLoading(false);
    // eslint-disable-next-line
  }, [senderQuizId, field, quizId, quizName, onPercentageUpdate]);

  return (
    <>
      {!loading && (
        <XAxisContainer>
          <XAxisLine>
            <Indicator position={position}>
              {profilePicture ? (
                <ProfilePicture src={profilePicture} $borderColor={colorOne} />
              ) : (
                <SecPic $borderColor={colorOne}>
                  <P3 className="mb-0">{displayName ? displayName[0] : "?"}</P3>
                </SecPic>
              )}
            </Indicator>
            {position2 && (
              <Indicator position={position2}>
                {positionsOverlap() ? (
                  <GradientCircle
                    gradientColors={{
                      color1: colorOne,
                      color2: colorTwo,
                    }}
                  >
                    <StarBorderIcon style={{ fontSize: "18px" }} />
                  </GradientCircle>
                ) : profilePicture2 ? (
                  <ProfilePicture
                    src={profilePicture2}
                    $borderColor={colorTwo}
                  />
                ) : (
                  <SecPic $borderColor={colorTwo}>
                    <P3 className="mb-0">
                      {displayName2 ? displayName2[0] : "?"}
                    </P3>
                  </SecPic>
                )}
              </Indicator>
            )}
          </XAxisLine>
          <LabelContainer>
            <Label textAlign={"left"}>{renderLabel(leftLabel)}</Label>
            <Label textAlign={"right"}>{renderLabel(rightLabel)}</Label>
          </LabelContainer>
          <DescriptionContainer>
            <Description textAlign={"left"}>{leftDescription}</Description>
            <Description textAlign={"right"}>{rightDescription}</Description>
          </DescriptionContainer>
        </XAxisContainer>
      )}
    </>
  );
};

export default Axis;

export const LockedAxis = ({
  leftLabel,
  rightLabel,
  leftDescription,
  rightDescription,
  colorOne,
  colorTwo,
  onClick,
}) => {
  const handleClick = async (e) => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (err) {
        // Silently fail - haptics are optional
      }
    }

    if (onClick) onClick(e);
  };
  return (
    <div onClick={handleClick}>
      <XAxisContainer>
        <XAxisLine>
          <Indicator position={"50%"}>
            <LockedCircle
              $background={colorOne}
              $transitionBackground={colorTwo}
            >
              <LockIcon style={{ fontSize: 16, color: White05 }} />
            </LockedCircle>
          </Indicator>
        </XAxisLine>
        <LabelContainer>
          <Label textAlign={"left"}>
            {typeof leftLabel === "string" && leftLabel.includes("<br")
              ? leftLabel.split(/<br\s*\/?>(?![^<]*>)/i).map((part, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <br />}
                    {part}
                  </React.Fragment>
                ))
              : leftLabel}
          </Label>
          <Label textAlign={"right"}>
            {typeof rightLabel === "string" && rightLabel.includes("<br")
              ? rightLabel.split(/<br\s*\/?>(?![^<]*>)/i).map((part, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <br />}
                    {part}
                  </React.Fragment>
                ))
              : rightLabel}
          </Label>
        </LabelContainer>
        <DescriptionContainer>
          <Description textAlign={"left"}>{leftDescription}</Description>
          <Description textAlign={"right"}>{rightDescription}</Description>
        </DescriptionContainer>
      </XAxisContainer>
    </div>
  );
};
